import { useQuery } from "@tanstack/react-query";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useStaffGetInvoiceReceipt } from "libs/api/src/lib/hooks/staff/useStaffGetInvoiceReceipt";
import { useTranslation } from "react-i18next";

import { DotBadge } from "../../../atoms/DotBadge";
import { StatusToColor } from "../constants";
import { normalizePaymentStatusLabel } from "../helpers";

type InvoiceInfoSectionType = {
  memberId?: string;
  invoiceNumber: string;
};
const InvoiceInfoSection = ({
  memberId,
  invoiceNumber,
}: InvoiceInfoSectionType) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const { data: member } = useQuery(
    apiV2.staff.member.queries.idDeprecated(memberId),
  );

  const { data: invoice } = useStaffGetInvoiceReceipt({
    api,
    invoiceNumber,
  });

  if (!invoice) return null;

  return (
    <>
      <div className="mt-6 flex items-center justify-between">
        <div className="inline-flex flex-col items-start gap-2">
          <div className="text-base font-medium text-gray-500">
            {t("components.paymentsSidebarProvider.invoice.title")}
          </div>
          <div className="text-lg font-semibold text-gray-950">
            #{invoice?.number}
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <div className="text-sm font-medium text-gray-500">
              {t("components.paymentsSidebarProvider.invoice.due")}
            </div>
            <div className="text-base font-semibold text-gray-950">
              {invoice?.issueDate}
            </div>
          </div>
        </div>
        {invoice?.status ? (
          <DotBadge intent={StatusToColor[invoice.status]}>
            {normalizePaymentStatusLabel(invoice.status)}
          </DotBadge>
        ) : null}
      </div>
      <div className="mt-6 flex justify-between text-sm">
        <div>
          <div className="font-medium text-gray-500">
            {t("components.paymentsSidebarProvider.invoice.from")}
          </div>
          <div className="font-semibold">{club.company.name}</div>
          <div className="font-semibold">{club.company.addressLine1}</div>
          <div className="font-semibold">{club.company.addressLine2}</div>
          <div className="font-semibold">{club.company.postCode}</div>
          <div className="font-semibold">{club.company.city}</div>
          <div className="whitespace-nowrap font-semibold">
            {t("components.paymentsSidebarProvider.invoice.salesTaxNumber")}
            {club.company.vatNumber}
          </div>
        </div>

        {member ? (
          <div className="text-right">
            <div className="font-medium text-gray-500">
              {t("components.paymentsSidebarProvider.invoice.to")}
            </div>
            <div className="font-semibold">
              {`${member?.firstName} ${member?.lastName}`}
            </div>
            <div className="font-semibold">{member?.email}</div>
            <div className="font-semibold">
              {member?.personalNumber ? `#${member.personalNumber}` : ""}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default InvoiceInfoSection;
